import { FRANCHISE_OVERRIDE_KEY } from '../exports/Constants';

// MR-environment hosts (mr-<branch>-shop.foodamigos.io, mr-<branch>-online-shop.foodamigos.io) are
// flat: no franchise subdomain, no franchise custom domain, and every MR gets a fresh clone of the dev
// database. So off production a browser picks its franchise with `?franchise=<slug>` (plus
// `&shop_domain=<root>` for a slug that exists on several roots), the same idea as storefront-web's
// `?store=`. The pick is kept in localStorage so reloads, new tabs and payment returns keep it.

export const FRANCHISE_OVERRIDE_PARAM = 'franchise';
export const SHOP_DOMAIN_OVERRIDE_PARAM = 'shop_domain';

export type FranchiseOverride = { franchise_slug: string; shop_domain?: string };

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const HOSTNAME = /^(?=.{1,253}$)[a-z0-9-]+(\.[a-z0-9-]+)+$/;

export const normaliseFranchiseSlug = (value?: string | null): string | null => {
  const slug = value?.trim().toLowerCase() ?? '';

  return slug.length <= 100 && SLUG.test(slug) ? slug : null;
};

export const normaliseShopDomain = (value?: string | null): string | null => {
  const domain = value?.trim().toLowerCase() ?? '';

  return HOSTNAME.test(domain) ? domain : null;
};

const toOverride = (slug?: string | null, shopDomain?: string | null): FranchiseOverride | null => {
  const franchiseSlug = normaliseFranchiseSlug(slug);

  if (!franchiseSlug) {
    return null;
  }

  const domain = normaliseShopDomain(shopDomain);

  return domain ? { franchise_slug: franchiseSlug, shop_domain: domain } : { franchise_slug: franchiseSlug };
};

/**
 * Stores (or, for an empty `?franchise=`, clears) the pick from the URL, then drops both params from the
 * address bar so they don't travel into shared links, analytics page URLs or payment return URLs. An
 * invalid value is ignored and leaves an earlier pick in place.
 */
export function captureFranchiseOverride(win: Window = window): void {
  // A Production bundle never reads the param or the stored pick. The check compares with the literal,
  // inline, rather than `ENVIRONMENTS.PROD` or a helper: the bundler folds only that form, so the body
  // below is dropped from the production bundle instead of shipping behind a runtime check.
  if (import.meta.env.VITE_MODE === 'Production') {
    return;
  }

  const url = new URL(win.location.href);

  if (!url.searchParams.has(FRANCHISE_OVERRIDE_PARAM)) {
    return;
  }

  const value = url.searchParams.get(FRANCHISE_OVERRIDE_PARAM);
  const override = toOverride(value, url.searchParams.get(SHOP_DOMAIN_OVERRIDE_PARAM));

  // Storage can throw under some privacy settings, and this runs before the app boots.
  try {
    if (!value?.trim()) {
      win.localStorage.removeItem(FRANCHISE_OVERRIDE_KEY);
    } else if (override) {
      win.localStorage.setItem(FRANCHISE_OVERRIDE_KEY, JSON.stringify(override));
    }
  } catch {
    // Without storage the pick can't be kept; the page falls back to the hostname.
  }

  url.searchParams.delete(FRANCHISE_OVERRIDE_PARAM);
  url.searchParams.delete(SHOP_DOMAIN_OVERRIDE_PARAM);
  win.history.replaceState(win.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

/** The stored pick, re-validated because localStorage is client-controlled. */
export function readFranchiseOverride(win: Window = window): FranchiseOverride | null {
  // Inline literal for the same reason as in captureFranchiseOverride.
  if (import.meta.env.VITE_MODE === 'Production') {
    return null;
  }

  try {
    const stored = JSON.parse(win.localStorage.getItem(FRANCHISE_OVERRIDE_KEY) ?? 'null');

    return toOverride(stored?.franchise_slug, stored?.shop_domain);
  } catch {
    return null;
  }
}
